import Paper from '@/components/Paper'
import {
    createColumns,
    DataTableColumn,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatCurrency } from '@/utils/function'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { getMonthlyAllocatedRevenue } from '../../../services'
import { SalesTargetFilters } from '../../../types'
import { ExcelExportButton } from '../ExportButtons'

interface RevenueRow {
    id: string
    type: string
    label: string
    [key: string]: string | number // dynamic month columns
}

const RevenueTarget: React.FC<SalesTargetFilters> = ({ ...filters }) => {
    const { t } = useTranslation()
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BI_REVENUE_ALLOCATIONS,
            'allocated-revenue',
            filters.employee_id ?? [],
            filters.gte,
            filters.lte,
        ],
        queryFn: () =>
            getMonthlyAllocatedRevenue({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters.employee_id ?? [],
            }),
        staleTime: 5 * 60 * 1000,
    })

    // Generate month columns based on date range
    const monthColumns = useMemo(() => {
        if (!filters.gte || !filters.lte) return []
        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const months: string[] = []
        let current = start.startOf('month')

        while (current.isBefore(end) || current.isSame(end, 'month')) {
            months.push(current.format('YYYY-MM'))
            current = current.add(1, 'month')
        }

        return months
    }, [filters.gte, filters.lte])

    // Aggregate all employee data
    const aggregatedData = useMemo(() => {
        if (!data || data.length === 0) return null

        const totals: Record<
            string,
            {
                renewal_revenue: number
                new_sale_revenue: number
                upsell_revenue: number
                total_revenue: number
            }
        > = {}

        monthColumns.forEach((month) => {
            totals[month] = {
                renewal_revenue: 0,
                new_sale_revenue: 0,
                upsell_revenue: 0,
                total_revenue: 0,
            }
        })

        data.forEach((employee) => {
            employee.monthly_breakdown.forEach((breakdown) => {
                if (totals[breakdown.revenue_month]) {
                    totals[breakdown.revenue_month].renewal_revenue +=
                        breakdown.renewal_revenue
                    totals[breakdown.revenue_month].new_sale_revenue +=
                        breakdown.new_sale_revenue
                    totals[breakdown.revenue_month].upsell_revenue +=
                        breakdown.upsell_revenue
                    totals[breakdown.revenue_month].total_revenue +=
                        breakdown.total_revenue
                }
            })
        })

        return totals
    }, [data, monthColumns])

    // Calculate cumulative totals
    const cumulativeTotals = useMemo(() => {
        if (!aggregatedData)
            return { renewal: 0, newSale: 0, upsell: 0, total: 0 }

        const totals = { renewal: 0, newSale: 0, upsell: 0, total: 0 }

        Object.values(aggregatedData).forEach((monthData) => {
            totals.renewal += monthData.renewal_revenue
            totals.newSale += monthData.new_sale_revenue
            totals.upsell += monthData.upsell_revenue
            totals.total += monthData.total_revenue
        })

        return totals
    }, [aggregatedData])

    const periodLabel = `${t('employees.performance.cumulative')} ${monthColumns.length}M, ${dayjs(filters.gte).year()}`

    // Transform data to rows format for DataTableCustomized
    const tableRows = useMemo<RevenueRow[]>(() => {
        if (!aggregatedData) return []

        const rows: RevenueRow[] = [
            {
                id: 'renewal',
                type: 'renewal',
                label: t('employees.performance.renewal_allocated_revenue'),
                cumulative: cumulativeTotals.renewal,
            },
            {
                id: 'new_sale',
                type: 'new_sale',
                label: t('employees.performance.new_sale_allocated_revenue'),
                cumulative: cumulativeTotals.newSale,
            },
            {
                id: 'upsell',
                type: 'upsell',
                label: t('employees.performance.upsell_allocated_revenue'),
                cumulative: cumulativeTotals.upsell,
            },
        ]

        // Add month data to each row
        monthColumns.forEach((month) => {
            rows[0][month] = aggregatedData[month].renewal_revenue
            rows[1][month] = aggregatedData[month].new_sale_revenue
            rows[2][month] = aggregatedData[month].upsell_revenue
        })

        return rows
    }, [aggregatedData, monthColumns, cumulativeTotals, t])

    // Build columns dynamically
    const columns = useMemo<DataTableColumn<RevenueRow>[]>(() => {
        const cols: DataTableColumn<RevenueRow>[] = [
            {
                key: 'label',
                title: t('employees.performance.revenue_type'),
                width: 280,
                headerClassName: 'text-left',
                cellClassName: 'font-medium',
            },
        ]

        // Add month columns
        monthColumns.forEach((month) => {
            cols.push({
                key: month,
                title: `${t('common.month')} ${dayjs(month).format('M')}`,
                width: 120,
                headerClassName: 'text-right',
                cellClassName: 'text-right',
                render: (value) => formatCurrency(value as number),
            })
        })

        // Add cumulative column
        cols.push({
            key: 'cumulative',
            title: periodLabel,
            width: 150,
            headerClassName: 'text-right bg-blue-50',
            cellClassName: 'text-right font-semibold bg-blue-50/50',
            render: (value) => formatCurrency(value as number),
        })

        return createColumns<RevenueRow>(cols)
    }, [monthColumns, t, periodLabel])

    // Footer row with totals
    const footerRow = useMemo(() => {
        if (!aggregatedData) return undefined

        const cells: Record<string, React.ReactNode> = {
            label: t('employees.performance.total_allocated_revenue'),
        }

        monthColumns.forEach((month) => {
            cells[month] = formatCurrency(aggregatedData[month].total_revenue)
        })

        cells.cumulative = (
            <span className="font-bold">
                {formatCurrency(cumulativeTotals.total)}
            </span>
        )

        return {
            cells,
            cellClassName: 'text-right',
        }
    }, [aggregatedData, monthColumns, cumulativeTotals, t])

    // Prepare export data
    const exportData = useMemo(() => {
        if (!aggregatedData) return []

        const rows = [
            {
                [t('employees.performance.revenue_type')]: t(
                    'employees.performance.renewal_allocated_revenue'
                ),
                ...monthColumns.reduce(
                    (acc, month) => ({
                        ...acc,
                        [`${t('common.month')} ${dayjs(month).format('M')}`]:
                            aggregatedData[month].renewal_revenue,
                    }),
                    {}
                ),
                [periodLabel]: cumulativeTotals.renewal,
            },
            {
                [t('employees.performance.revenue_type')]: t(
                    'employees.performance.new_sale_allocated_revenue'
                ),
                ...monthColumns.reduce(
                    (acc, month) => ({
                        ...acc,
                        [`${t('common.month')} ${dayjs(month).format('M')}`]:
                            aggregatedData[month].new_sale_revenue,
                    }),
                    {}
                ),
                [periodLabel]: cumulativeTotals.newSale,
            },
            {
                [t('employees.performance.revenue_type')]: t(
                    'employees.performance.upsell_allocated_revenue'
                ),
                ...monthColumns.reduce(
                    (acc, month) => ({
                        ...acc,
                        [`${t('common.month')} ${dayjs(month).format('M')}`]:
                            aggregatedData[month].upsell_revenue,
                    }),
                    {}
                ),
                [periodLabel]: cumulativeTotals.upsell,
            },
            {
                [t('employees.performance.revenue_type')]: t(
                    'employees.performance.total_allocated_revenue'
                ),
                ...monthColumns.reduce(
                    (acc, month) => ({
                        ...acc,
                        [`${t('common.month')} ${dayjs(month).format('M')}`]:
                            aggregatedData[month].total_revenue,
                    }),
                    {}
                ),
                [periodLabel]: cumulativeTotals.total,
            },
        ]
        return rows
    }, [aggregatedData, monthColumns, cumulativeTotals, periodLabel, t])

    const renderComponent = renderState()
    if (renderComponent) return renderComponent

    if (!aggregatedData) return null

    return (
        <Paper className="space-y-4">
            <div className="flex items-center justify-between">
                <Typogaphy size="medium" className="font-medium">
                    {t('employees.performance.revenue_target')}
                </Typogaphy>
                <ExcelExportButton
                    data={exportData}
                    filename="revenue-target"
                />
            </div>

            <DataTableCustomized
                columns={columns}
                rows={tableRows}
                rowKey={(row) => row.id}
                footerRow={footerRow}
                size="small"
            />
        </Paper>
    )
}

export default RevenueTarget
